import { apiClient } from '@borradh-workspace/api-client';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * Claire's approval cards that are still waiting for the owner.
 *
 * Approving is a chat message (the model holds the token); these endpoints
 * only say which cards are unanswered, so a card the owner has not answered
 * stays on screen after they send something else or reload, and record a
 * Cancel so a declined card does not come back.
 */

export interface ApprovalCardStatuses {
  open: string[];
  expired: string[];
}

export const approvalCardStatusesQueryOptions = (
  tokens: readonly string[],
  revision: string
) =>
  queryOptions({
    queryKey: queryKeys.assistant.approvalCardStatuses(tokens, revision),
    queryFn: () =>
      apiClient.post<ApprovalCardStatuses>('assistant/approval-cards/status', {
        tokens,
      }),
    enabled: tokens.length > 0,
    staleTime: 60 * 1000,
    // A new revision keeps showing the last answer until it lands, so an
    // earlier card does not blink out and back at every turn.
    placeholderData: keepPreviousData,
  });

export const useApprovalCardStatuses = (
  tokens: readonly string[],
  revision: string
) => {
  const query = useQuery(approvalCardStatusesQueryOptions(tokens, revision));
  return { statuses: query.data ?? null };
};

export const useDeclineApprovalCard = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (token: string) =>
      apiClient.post<{ declined: boolean }>(
        `assistant/approval-cards/${encodeURIComponent(token)}/decline`,
        {}
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.assistant.approvalCards(),
      });
    },
  });
  return { declineApprovalCardAsync: mutation.mutateAsync };
};
